/**
 * Calendar Page
 *
 * Displays tasks in a monthly/weekly calendar view with drag-drop rescheduling.
 * Supports both custom CalendarGrid and react-big-calendar via feature flag.
 */

import { useState, useCallback, useMemo, lazy, Suspense } from 'react';
import {
  Typography,
  Card,
  Space,
  Button,
  Select,
  Tag,
  List,
  Empty,
  Segmented,
  Spin,
  Dropdown,
  Modal,
  Input,
  message,
  DatePicker,
  Checkbox,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  ReloadOutlined,
  CalendarOutlined,
  UnorderedListOutlined,
  ExportOutlined,
  LinkOutlined,
  DownloadOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { instancesApi, categoriesApi } from '@api/index';
import { calendarApi } from '@api/calendar';
import { TaskDrawer } from '@components/drawers';
import { CalendarGrid } from '@components/calendar';
import { features } from '@/config/features';
import type { ProcessInstance, ProcessStatus } from '@/types';

// Lazy load BigCalendarView
const BigCalendarView = lazy(() => import('@components/calendar/BigCalendarView'));

// Loading fallback
const CalendarLoading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
    <Spin size="large" />
  </div>
);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const statusColors: Record<ProcessStatus, string> = {
  pending: 'default',
  in_progress: 'processing',
  completed: 'success',
  postponed: 'warning',
  delayed: 'error',
  cancelled: 'default',
  ignored: 'default',
};

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>();

  // Calendar subscription/export state
  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [subscriptionUrl, setSubscriptionUrl] = useState<string>('');
  const [webcalUrl, setWebcalUrl] = useState<string>('');
  const [exportDateRange, setExportDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [exportIncludeCompleted, setExportIncludeCompleted] = useState(false);
  const [loadingSubscription, setLoadingSubscription] = useState(false);

  const year = currentDate.year();
  const month = currentDate.month() + 1; // dayjs months are 0-indexed

  // Fetch calendar data (for custom CalendarGrid)
  const { data: calendarData, isLoading: calendarLoading, refetch: refetchCalendar } = useQuery({
    queryKey: ['instances', 'calendar', year, month, categoryFilter],
    queryFn: () => instancesApi.getCalendar(year, month, { categoryId: categoryFilter }),
    enabled: !features.useBigCalendar,
  });

  // Fetch all tasks (for BigCalendarView)
  const { data: allTasks = [], isLoading: tasksLoading, refetch: refetchTasks } = useQuery({
    queryKey: ['instances', 'all', categoryFilter],
    queryFn: () => instancesApi.list({
      categoryId: categoryFilter,
      includeCompleted: true,
    }),
    enabled: features.useBigCalendar,
  });

  const isLoading = features.useBigCalendar ? tasksLoading : calendarLoading;
  const refetch = features.useBigCalendar ? refetchTasks : refetchCalendar;

  // Fetch categories for filter
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
  });

  // Build a map of date -> instances for quick lookup (custom CalendarGrid)
  const instancesByDate = useMemo(() => {
    const map = new Map<string, ProcessInstance[]>();
    calendarData?.days.forEach((day) => {
      map.set(day.date, day.instances);
    });
    return map;
  }, [calendarData]);

  // Get instances for selected date
  const selectedDateInstances = selectedDate
    ? instancesByDate.get(selectedDate.format('YYYY-MM-DD')) || []
    : [];

  const handleNavigate = useCallback(
    (direction: 'prev' | 'next') => {
      const unit = viewMode === 'week' ? 'week' : 'month';
      setCurrentDate((prev) =>
        direction === 'prev' ? prev.subtract(1, unit) : prev.add(1, unit)
      );
    },
    [viewMode]
  );

  const handleDateSelect = useCallback((date: Dayjs | Date) => {
    if (date instanceof Date) {
      setSelectedDate(dayjs(date));
    } else {
      setSelectedDate(date);
    }
  }, []);

  const handleTaskClick = useCallback((task: ProcessInstance) => {
    setSelectedTaskId(task.id);
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedTaskId(undefined);
  }, []);

  // Calendar subscription handler
  const handleSubscribe = useCallback(async () => {
    setLoadingSubscription(true);
    try {
      const data = await calendarApi.getToken();
      setSubscriptionUrl(data.feedUrl);
      setWebcalUrl(data.webcalUrl);
      setSubscribeModalOpen(true);
    } catch (error) {
      message.error('Failed to generate subscription URL');
    } finally {
      setLoadingSubscription(false);
    }
  }, []);

  // Copy URL to clipboard
  const handleCopyUrl = useCallback((url: string) => {
    navigator.clipboard.writeText(url);
    message.success('URL copied to clipboard');
  }, []);

  // Calendar export handler
  const handleExport = useCallback(async () => {
    try {
      const options = {
        start: exportDateRange?.[0]?.format('YYYY-MM-DD'),
        end: exportDateRange?.[1]?.format('YYYY-MM-DD'),
        completed: exportIncludeCompleted,
      };
      const blob = await calendarApi.downloadExport(options);

      // Download the file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bizops-calendar-${dayjs().format('YYYY-MM-DD')}.ics`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setExportModalOpen(false);
      message.success('Calendar exported successfully');
    } catch (error) {
      message.error('Failed to export calendar');
    }
  }, [exportDateRange, exportIncludeCompleted]);

  // Export menu items
  const exportMenuItems: MenuProps['items'] = [
    {
      key: 'subscribe',
      icon: <LinkOutlined />,
      label: 'Subscribe to Calendar',
      onClick: handleSubscribe,
    },
    {
      key: 'export',
      icon: <DownloadOutlined />,
      label: 'Export to iCal',
      onClick: () => setExportModalOpen(true),
    },
  ];

  // Get period display text
  const periodDisplay = useMemo(() => {
    if (viewMode === 'week') {
      const startOfWeek = currentDate.startOf('week');
      const endOfWeek = currentDate.endOf('week');
      if (startOfWeek.month() === endOfWeek.month()) {
        return `${startOfWeek.format('MMM D')} - ${endOfWeek.format('D, YYYY')}`;
      }
      return `${startOfWeek.format('MMM D')} - ${endOfWeek.format('MMM D, YYYY')}`;
    }
    return currentDate.format('MMMM YYYY');
  }, [currentDate, viewMode]);

  // Render BigCalendarView (full-page calendar with built-in navigation)
  if (features.useBigCalendar) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            Calendar
          </Title>

          <Space>
            <Select
              value={categoryFilter}
              onChange={setCategoryFilter}
              style={{ width: 180 }}
              placeholder="All Categories"
              allowClear
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
            />
            <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
              <Button icon={<ExportOutlined />} loading={loadingSubscription}>
                Export
              </Button>
            </Dropdown>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
              Refresh
            </Button>
          </Space>
        </div>

        {/* BigCalendarView */}
        <div style={{ flex: 1 }}>
          <Suspense fallback={<CalendarLoading />}>
            <BigCalendarView
              tasks={allTasks}
              loading={isLoading}
              onTaskClick={handleTaskClick}
              onDateSelect={handleDateSelect}
            />
          </Suspense>
        </div>

        {/* Task Drawer */}
        <TaskDrawer
          open={drawerOpen}
          instanceId={selectedTaskId}
          mode="view"
          onClose={handleCloseDrawer}
          onSaved={() => refetch()}
        />

        {/* Calendar Subscription Modal */}
        <Modal
          title="Subscribe to Calendar"
          open={subscribeModalOpen}
          onCancel={() => setSubscribeModalOpen(false)}
          footer={[
            <Button key="close" onClick={() => setSubscribeModalOpen(false)}>
              Close
            </Button>,
          ]}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text strong>Subscribe with calendar app:</Text>
              <div style={{ marginTop: 8 }}>
                <Input.Group compact>
                  <Input
                    value={webcalUrl}
                    readOnly
                    style={{ width: 'calc(100% - 32px)' }}
                  />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => handleCopyUrl(webcalUrl)}
                  />
                </Input.Group>
              </div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                Click copy and paste into your calendar app (Google Calendar, Apple Calendar, Outlook)
              </Text>
            </div>
            <div>
              <Text strong>Direct feed URL:</Text>
              <div style={{ marginTop: 8 }}>
                <Input.Group compact>
                  <Input
                    value={subscriptionUrl}
                    readOnly
                    style={{ width: 'calc(100% - 32px)' }}
                  />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => handleCopyUrl(subscriptionUrl)}
                  />
                </Input.Group>
              </div>
            </div>
            <div>
              <Button type="primary" href={webcalUrl} target="_blank">
                <LinkOutlined /> Open in Calendar App
              </Button>
            </div>
          </Space>
        </Modal>

        {/* Calendar Export Modal */}
        <Modal
          title="Export Calendar"
          open={exportModalOpen}
          onCancel={() => setExportModalOpen(false)}
          onOk={handleExport}
          okText="Download"
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text strong>Date Range:</Text>
              <div style={{ marginTop: 8 }}>
                <RangePicker
                  value={exportDateRange}
                  onChange={(dates) => setExportDateRange(dates as [Dayjs, Dayjs] | null)}
                  style={{ width: '100%' }}
                  placeholder={['Start date', 'End date']}
                />
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Leave empty to export next 90 days
              </Text>
            </div>
            <div>
              <Checkbox
                checked={exportIncludeCompleted}
                onChange={(e) => setExportIncludeCompleted(e.target.checked)}
              >
                Include completed tasks
              </Checkbox>
            </div>
          </Space>
        </Modal>
      </div>
    );
  }

  // Render custom CalendarGrid (original implementation)
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Calendar
        </Title>

        <Space>
          <Segmented
            value={viewMode}
            onChange={(v) => setViewMode(v as 'month' | 'week')}
            options={[
              { value: 'month', icon: <CalendarOutlined />, label: 'Month' },
              { value: 'week', icon: <UnorderedListOutlined />, label: 'Week' },
            ]}
          />
          <Select
            value={categoryFilter}
            onChange={setCategoryFilter}
            style={{ width: 180 }}
            placeholder="All Categories"
            allowClear
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
          />
          <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
            <Button icon={<ExportOutlined />} loading={loadingSubscription}>
              Export
            </Button>
          </Dropdown>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
            Refresh
          </Button>
        </Space>
      </div>

      {/* Calendar navigation */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <Button
          icon={<LeftOutlined />}
          onClick={() => handleNavigate('prev')}
        />
        <Title level={4} style={{ margin: 0, minWidth: 260, textAlign: 'center' }}>
          {periodDisplay}
        </Title>
        <Button
          icon={<RightOutlined />}
          onClick={() => handleNavigate('next')}
        />
        <Button onClick={() => setCurrentDate(dayjs())}>Today</Button>
      </div>

      {/* Main content */}
      <div style={{ display: 'flex', flex: 1, gap: 24, overflow: 'hidden' }}>
        {/* Calendar */}
        <div style={{ flex: 2, overflow: 'auto' }}>
          <CalendarGrid
            currentDate={currentDate}
            view={viewMode}
            instancesByDate={instancesByDate}
            onDateSelect={handleDateSelect}
            onTaskClick={handleTaskClick}
            selectedDate={selectedDate}
          />
        </div>

        {/* Selected date tasks */}
        <div style={{ flex: 1, minWidth: 300 }}>
          <Card
            title={
              selectedDate
                ? selectedDate.format('dddd, MMMM D, YYYY')
                : 'Select a date'
            }
            style={{ height: '100%' }}
          >
            {!selectedDate ? (
              <Empty description="Click on a date to see tasks" />
            ) : selectedDateInstances.length === 0 ? (
              <Empty description="No tasks on this date" />
            ) : (
              <List
                dataSource={selectedDateInstances}
                renderItem={(task) => (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleTaskClick(task)}
                    actions={[
                      <Tag
                        key="status"
                        color={statusColors[task.status]}
                      >
                        {task.status.replace('_', ' ')}
                      </Tag>,
                    ]}
                  >
                    <List.Item.Meta
                      title={task.template?.title}
                      description={
                        <Space>
                          {task.template?.category && (
                            <Tag color={task.template.category.color}>
                              {task.template.category.name}
                            </Tag>
                          )}
                          {task.assignedUser && (
                            <Text type="secondary">
                              {task.assignedUser.fullName}
                            </Text>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </div>
      </div>

      {/* Task Drawer */}
      <TaskDrawer
        open={drawerOpen}
        instanceId={selectedTaskId}
        mode="view"
        onClose={handleCloseDrawer}
        onSaved={() => refetch()}
      />

      {/* Calendar Subscription Modal */}
      <Modal
        title="Subscribe to Calendar"
        open={subscribeModalOpen}
        onCancel={() => setSubscribeModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setSubscribeModalOpen(false)}>
            Close
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>Subscribe with calendar app:</Text>
            <div style={{ marginTop: 8 }}>
              <Input.Group compact>
                <Input
                  value={webcalUrl}
                  readOnly
                  style={{ width: 'calc(100% - 32px)' }}
                />
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyUrl(webcalUrl)}
                />
              </Input.Group>
            </div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              Click copy and paste into your calendar app (Google Calendar, Apple Calendar, Outlook)
            </Text>
          </div>
          <div>
            <Text strong>Direct feed URL:</Text>
            <div style={{ marginTop: 8 }}>
              <Input.Group compact>
                <Input
                  value={subscriptionUrl}
                  readOnly
                  style={{ width: 'calc(100% - 32px)' }}
                />
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyUrl(subscriptionUrl)}
                />
              </Input.Group>
            </div>
          </div>
          <div>
            <Button type="primary" href={webcalUrl} target="_blank">
              <LinkOutlined /> Open in Calendar App
            </Button>
          </div>
        </Space>
      </Modal>

      {/* Calendar Export Modal */}
      <Modal
        title="Export Calendar"
        open={exportModalOpen}
        onCancel={() => setExportModalOpen(false)}
        onOk={handleExport}
        okText="Download"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Date Range:</Text>
            <div style={{ marginTop: 8 }}>
              <RangePicker
                value={exportDateRange}
                onChange={(dates) => setExportDateRange(dates as [Dayjs, Dayjs] | null)}
                style={{ width: '100%' }}
                placeholder={['Start date', 'End date']}
              />
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Leave empty to export next 90 days
            </Text>
          </div>
          <div>
            <Checkbox
              checked={exportIncludeCompleted}
              onChange={(e) => setExportIncludeCompleted(e.target.checked)}
            >
              Include completed tasks
            </Checkbox>
          </div>
        </Space>
      </Modal>
    </div>
  );
}
